/**
 * ExportActions Component
 * Provides export options and actions (PDF, JSON, Print)
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { 
  FileDown, 
  Printer, 
  FileJson, 
  FileText, 
  Download,
  Loader,
  CheckCircle,
  AlertCircle
} from "lucide-react";

const ExportActions = ({
  onExportPDF,
  onExportJSON,
  onPrint,
  isExporting,
  exportDisabled,
  selectedCount,
  patientName,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  const handleExport = async (exportFn, type) => {
    setExportStatus({ type, status: "loading" });
    try {
      await exportFn();
      setExportStatus({ type, status: "success" });
      setTimeout(() => setExportStatus(null), 3000);
    } catch (error) {
      setExportStatus({ type, status: "error", message: error.message });
      setTimeout(() => setExportStatus(null), 5000);
    }
    setShowExportMenu(false);
  };

  const getButtonStatus = (type) => {
    if (exportStatus?.type === type) {
      return exportStatus.status;
    }
    return null;
  };

  return (
    <div className="export-actions">
      <div className="export-actions__info">
        <div className="export-summary">
          <span className="selected-count">
            {selectedCount} consultation{selectedCount !== 1 ? "s" : ""} sélectionnée{selectedCount !== 1 ? "s" : ""}
          </span>
          {patientName && (
            <span className="patient-name">
              for {patientName}
            </span>
          )}
        </div>
      </div>

      <div className="export-actions__buttons">
        {/* PDF Export Button */}
        <button
          type="button"
          className={`export-btn export-btn--pdf ${getButtonStatus("pdf") || ""}`}
          onClick={() => handleExport(onExportPDF, "pdf")}
          disabled={exportDisabled || isExporting}
        >
          {getButtonStatus("pdf") === "loading" ? (
            <Loader size={16} className="spin" />
          ) : getButtonStatus("pdf") === "success" ? (
            <CheckCircle size={16} />
          ) : getButtonStatus("pdf") === "error" ? (
            <AlertCircle size={16} />
          ) : (
            <FileText size={16} />
          )}
          <span>Exporter PDF</span>
        </button>

        {/* JSON Export Button */}
        <button
          type="button"
          className={`export-btn export-btn--json ${getButtonStatus("json") || ""}`}
          onClick={() => handleExport(onExportJSON, "json")}
          disabled={exportDisabled || isExporting}
        >
          {getButtonStatus("json") === "loading" ? (
            <Loader size={16} className="spin" />
          ) : getButtonStatus("json") === "success" ? (
            <CheckCircle size={16} />
          ) : getButtonStatus("json") === "error" ? (
            <AlertCircle size={16} />
          ) : (
            <FileJson size={16} />
          )}
          <span>Exporter JSON</span>
        </button>

        {/* Print Button */}
        <button
          type="button"
          className={`export-btn export-btn--print ${getButtonStatus("print") || ""}`}
          onClick={() => handleExport(onPrint, "print")}
          disabled={exportDisabled || isExporting}
        >
          {getButtonStatus("print") === "loading" ? (
            <Loader size={16} className="spin" />
          ) : getButtonStatus("print") === "success" ? (
            <CheckCircle size={16} />
          ) : (
            <Printer size={16} />
          )}
          <span>Imprimer</span>
        </button>

        {/* Quick Export Dropdown */}
        <div className="export-dropdown">
          <button
            type="button"
            className="export-btn export-btn--more"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exportDisabled || isExporting}
          >
            <Download size={16} />
            <span>Plus</span>
          </button>

          {showExportMenu && (
            <div className="export-dropdown__menu">
              <button
                type="button"
                onClick={() => handleExport(onExportPDF, "pdf")}
              >
                <FileText size={14} />
                <span>PDF (Standard)</span>
              </button>
              <button
                type="button"
                onClick={() => handleExport(onExportJSON, "json")}
              >
                <FileJson size={14} />
                <span>JSON (Données uniquement)</span>
              </button>
              <div className="dropdown-divider" />
              <button
                type="button"
                onClick={() => handleExport(onPrint, "print")}
              >
                <Printer size={14} />
                <span>Aperçu avant impression</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {exportStatus?.status === "error" && (
        <div className="export-error">
          <AlertCircle size={14} />
          <span>{exportStatus.message || "L'export a échoué. Veuillez réessayer."}</span>
        </div>
      )}
    </div>
  );
};

ExportActions.propTypes = {
  onExportPDF: PropTypes.func.isRequired,
  onExportJSON: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
  isExporting: PropTypes.bool,
  exportDisabled: PropTypes.bool,
  selectedCount: PropTypes.number,
  patientName: PropTypes.string,
};

ExportActions.defaultProps = {
  isExporting: false,
  exportDisabled: false,
  selectedCount: 0,
  patientName: "",
};

export default ExportActions;
